import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { TONE_BORDER, TONE_SOFT } from './tone';

/**
 * A single figure with its name above it — the row of summary numbers at the
 * top of a report screen.
 *
 * The shape is the one Sales already uses (`builder/components/ui/
 * MetricCard.jsx`, the tiles on the Leads list): a tinted icon chip on the
 * left, a small uppercase label, a large bold figure, and — when the tile
 * filters something — an arrow in the corner saying so. The newer screens had
 * been drawing a plainer box, which is why a Labour report and the Leads list
 * did not look like the same product even after the colours matched.
 *
 * Before that, seven screens each shipped their own copy, disagreeing on label
 * size, value size, the gap between them, the resting background, and even the
 * name of the emphasis prop (`tone`, `accent`, `strong`).
 *
 * The figure is `text-xl sm:text-2xl` rather than MetricCard's
 * `text-2xl sm:text-3xl`: these rows go up to seven across (Labour Spend),
 * where the larger step wraps. On a wide screen they land close enough to read
 * as the same tile.
 *
 *   <Tile label="Outstanding" value="₹7,12,600" hint="Still owed"
 *         icon={<Wallet size={18} />} tone="warning" />
 *
 *   <Tile label="Cold" value={47} icon={<Snowflake size={18} />}
 *         onClick={() => filterBy('cold')} />   // gains the ↗ affordance
 */
const Tile = ({
  label,
  value,
  hint,
  icon,
  tone = 'neutral',
  onClick,
  className = ''
}) => {
  const interactive = typeof onClick === 'function';

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      className={`relative flex items-center gap-3 rounded-panel border p-3 ${
        TONE_BORDER[tone] ?? TONE_BORDER.neutral
      } ${interactive ? 'cursor-pointer transition-shadow hover:shadow-card' : ''} ${
        interactive ? 'pr-8' : ''
      } ${className}`}>
      {/* Hidden below sm: at phone width the chip costs more room than the
          figure it decorates, which is the trade MetricCard already makes. */}
      {icon && (
        <span
          className={`hidden h-9 w-9 shrink-0 items-center justify-center rounded-control sm:flex ${
            TONE_SOFT[tone] ?? TONE_SOFT.neutral
          }`}>
          {icon}
        </span>
      )}

      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium uppercase leading-tight tracking-wide text-gray-500">
          {label}
        </div>
        <div className="mt-0.5 text-xl font-bold leading-none tabular-nums text-gray-900 sm:text-2xl">
          {value}
        </div>
        {hint && <div className="mt-1 truncate text-xs text-gray-500">{hint}</div>}
      </div>

      {interactive && (
        <span
          aria-hidden="true"
          className="absolute bottom-2 right-2 inline-flex h-5 w-5 items-center justify-center rounded-pill bg-brand-100 text-brand-700">
          <ArrowUpRight size={14} />
        </span>
      )}
    </div>
  );
};

export default Tile;
